export const DELIVERY_SAVE_MAX_ATTEMPTS = 3;

const DELIVERY_SAVE_RETRY_BASE_DELAY_MS = 500;

type DeliverySaveRetryDetails = {
    attempt: number;
    maxAttempts: number;
    delayMs: number;
    error: Error;
};

type DeliverySaveRetryOptions = {
    maxAttempts?: number;
    wait?: (delayMs: number) => Promise<void>;
    onRetry?: (details: DeliverySaveRetryDetails) => void;
};

type ErrorLike = {
    message?: unknown;
    code?: unknown;
    details?: unknown;
    hint?: unknown;
};

function stringifyErrorValue(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

export function toDeliverySaveError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    const errorLike = error as ErrorLike | null | undefined;
    // i18n-ignore: technical-token -- normalized diagnostic is logged, never rendered directly.
    const message = stringifyErrorValue(errorLike?.message)
        || stringifyErrorValue(errorLike?.details)
        || stringifyErrorValue(errorLike?.hint)
        || 'Failed to save project';
    const normalizedError = new Error(message);

    const code = stringifyErrorValue(errorLike?.code);
    if (code) {
        (normalizedError as Error & { code?: string }).code = code;
    }

    return normalizedError;
}

export function isDeliverySaveStatementTimeout(error: unknown): boolean {
    const normalizedError = toDeliverySaveError(error);
    const code = stringifyErrorValue((normalizedError as Error & { code?: string }).code).toLowerCase();
    const message = normalizedError.message.toLowerCase();

    return code === '57014'
        || message.includes('statement timeout')
        || message.includes('canceling statement due to statement timeout');
}

export function getDeliverySaveRetryDelayMs(attempt: number): number {
    return DELIVERY_SAVE_RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt - 1));
}

export async function retryDeliverySaveTimeout<T>(
    operation: (attempt: number) => Promise<T>,
    options: DeliverySaveRetryOptions = {}
): Promise<T> {
    const maxAttempts = options.maxAttempts ?? DELIVERY_SAVE_MAX_ATTEMPTS;
    const wait = options.wait ?? ((delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)));

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            return await operation(attempt);
        } catch (error) {
            const normalizedError = toDeliverySaveError(error);

            if (!isDeliverySaveStatementTimeout(normalizedError) || attempt === maxAttempts) {
                throw normalizedError;
            }

            const delayMs = getDeliverySaveRetryDelayMs(attempt);
            options.onRetry?.({
                attempt,
                maxAttempts,
                delayMs,
                error: normalizedError,
            });
            await wait(delayMs);
        }
    }

    throw new Error('Delivery save retry exhausted');
}

export function getDeliverySaveErrorMessage(error: unknown): LocalizedMessage {
    if (isDeliverySaveStatementTimeout(error)) {
        return msg({ id: 'delivery.saveTimeout', message: 'Saving took longer than expected. Please try again in a moment.' });
    }

    return msg({ id: 'delivery.saveFailure', message: 'We could not save your project. Please try again. If this keeps happening, contact support.' });
}
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';
